import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { Modality, strToModality } from './modality';

export enum ObjectStatus {
    NEW = 'NEW', // object is new and not yet saved
    IMPORTED = 'IMPORTED', // object is imported from a DICOM source
    EXPORTED = 'EXPORTED', // object is exported to a DICOM source
}

export type GenerationModelDetails = {
    name: string;
    label: string;
    version?: string;
    isLatest?: boolean;
    customizationName?: string;
    inputModalities?: string[];
    outputModality?: string;
    /**
     * The connector processing task whose result this object is, when the
     * connector can attribute a saved dataset to it. Saving the object names
     * this task, and the connector records the dataset's origin from its own
     * record of the task.
     */
    provenanceTaskId?: string;
    /**
     * The task was given an input structure set, whose structures its result
     * can carry through: which structures the model itself produced cannot
     * then be told from the result.
     */
    resultCarriesInputStructures?: boolean;
};

/* base for DICOM modality objects */
export abstract class DicomBase {
    /**
     * The {@link Modality} of the DICOM object.\
     * This specifies the type of data the DICOM object contains
     * (0008,0060) https://dicom.innolitics.com/ciods/ct-image/general-series/00080060
     */
    public modality: Modality;

    /**
     * sopInstanceUid is an unique identifier given to each individual DICOM object.\
     * Each sopInstanceUid only has to be unique within the single {@link Series}.\
     * And so every DICOM object within the same {@link Series} should have its own unique sopInstanceUid.
     * NOTE: current rtviewer-front code expects SOP Instance UIDs to be unique globally, not just within a Series.
     */
    public sopInstanceUid: string;

    public datasetWrapper: DcmJsDatasetWrapper;

    public objectStatus: ObjectStatus = ObjectStatus.NEW;

    public generationModel: GenerationModelDetails | undefined;

    public get isNew(): boolean {
        return this.objectStatus === ObjectStatus.NEW;
    }

    public get isExported(): boolean {
        return this.objectStatus === ObjectStatus.EXPORTED;
    }

    public get isImported(): boolean {
        return this.objectStatus === ObjectStatus.IMPORTED;
    }

    public setExported(): void {
        this.objectStatus = ObjectStatus.EXPORTED;
    }

    public setNew(): void {
        this.objectStatus = ObjectStatus.NEW;
    }

    public getAsDicomBlob(): Blob {
        return this.datasetWrapper.asBlob();
    }

    constructor(
        sopUid: string,
        modality: string,
        datasetWrapper: DcmJsDatasetWrapper
    ) {
        this.sopInstanceUid = sopUid;
        this.modality = strToModality(modality);
        this.datasetWrapper = datasetWrapper;
    }
}
